import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Bug 11305: warn before unsaved changes are lost by leaving the page.
 *
 * While `when` is true this registers a blocker on the router's history
 * object. Any in-app navigation (main nav links, back/forward, history.push)
 * is held and `isLeaving` flips to true so the caller can open its own
 * confirmation modal. `confirmLeave()` lets the held navigation continue,
 * `cancelLeave()` drops it and stays on the page.
 *
 * The app runs react-router-dom 4.3.1 on top of `history` 5.x. The router's
 * <Prompt> is not reliable on that pairing (RR4 hands `history.block` a
 * string, history 5 expects a function), so this talks to `history.block`
 * directly using the v5 transaction API (`tx.retry()`). history 5 also
 * registers the browser's own beforeunload prompt for as long as a blocker
 * is active, which covers closing or reloading the tab with no extra code -
 * and because the blocker only exists while `when` is true, a clean page
 * never prompts.
 *
 * @param {object} history - the router history (props.history from a <Route>)
 * @param {boolean} when - true while the page has unsaved changes
 * @returns {{ isLeaving: boolean, confirmLeave: function, cancelLeave: function }}
 */
export default function useUnsavedChangesGuard(history, when) {
    const [pendingTransition, setPendingTransition] = useState(null);
    const unblockRef = useRef(null);

    useEffect(() => {
        if (!when || !history || typeof history.block !== 'function') {
            return undefined;
        }

        const unblock = history.block((tx) => {
            setPendingTransition(tx);
        });
        unblockRef.current = unblock;

        return () => {
            unblock();
            if (unblockRef.current === unblock) unblockRef.current = null;
            setPendingTransition(null);
        };
    }, [history, when]);

    const confirmLeave = useCallback(() => {
        const tx = pendingTransition;
        setPendingTransition(null);
        if (unblockRef.current) {
            unblockRef.current();
            unblockRef.current = null;
        }
        if (tx && typeof tx.retry === 'function') tx.retry();
    }, [pendingTransition]);

    const cancelLeave = useCallback(() => {
        setPendingTransition(null);
    }, []);

    return {
        isLeaving: pendingTransition !== null,
        confirmLeave,
        cancelLeave
    };
}
